import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useOutletContext } from 'react-router-dom'
import Swal from 'sweetalert2'
import { getThemeById } from '../../config/themes'
import { canRoundAcceptBet, isRoundNotYetOpen, isRoundCancelled, getLottoRoundClosedCardClass, getLottoRoundStatusColorClass, getRoundImage, lotteryService, type LotteryRound, type PublicHuayType } from '../../services/lotteryService'
import type { LobbyHuayOutletContext } from './ModernLobbyHuayLayout'
import { lookupHuayYoutubeLiveForRound, type HuayYoutubeLiveConfig } from '../../lib/liveTv'
import HuayLiveModal from '../../components/HuayLiveModal'
import HuayLiveBadge from '../../components/HuayLiveBadge'
import './ModernLobbyHuayListAllPage.css'

const IMG_PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

const CARD_COL = 'col-6 col-md-6 px-1 lobby-huay-col'
const CARD_COL_GRID = 'col-6 col-md-6 col-lg-4 col-xl-3 px-1 lobby-huay-col'
const CARD_STACK = 'd-grid gap-2 lobby-huay-col-stack'
const CARD_BASE = 'hh_hover lobby-huay-round-card'

/** แสดง countdown ที่อัปเดตทุกวินาที — ยังไม่เปิด: นับถอยหลังถึงเวลาเปิด, เปิดอยู่: นับถึงเวลาปิด, ยกเลิก/ปิดแล้ว: ข้อความสถานะ */
const computeCountdownText = (round: Pick<LotteryRound, 'status' | 'betStartTime' | 'betEndTime'>) => {
  if (isRoundCancelled(round)) return 'ยกเลิก'
  if (isRoundNotYetOpen(round)) {
    return lotteryService.formatTimeRemaining(round.betStartTime)
  }
  return canRoundAcceptBet(round) ? lotteryService.formatTimeRemaining(round.betEndTime) : 'ปิดรับแทง'
}

const CountdownDisplay: React.FC<{ round: Pick<LotteryRound, 'status' | 'betStartTime' | 'betEndTime'> }> = ({ round }) => {
  const [text, setText] = useState(() => computeCountdownText(round))

  useEffect(() => {
    const tick = () => {
      setText(computeCountdownText(round))
    }

    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [round])

  return <>{text}</>
}

function normalizeLabel(value: string): string {
  return String(value || '')
    .trim()
    .replace(/\s+/g, '')
    .replace(/[().]/g, '')
    .toLowerCase()
}

function formatCloseTime(betEndTime: string): string {
  const betEndDate = new Date(betEndTime)
  return betEndDate.toLocaleString('th-TH', {
    timeZone: 'Asia/Bangkok',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** หน้ารายการหวยวันนี้ (listall) — ใช้ API และ logic เดียวกับ user-classic /lotto, ต่างเฉพาะ UI แบบ modern */
const ModernLobbyHuayListAllPage: React.FC = () => {
  const { themeConfig, assetConfig } = useOutletContext<LobbyHuayOutletContext>()
  const location = useLocation()
  const navigate = useNavigate()
  const fromWinNumber = (location.state as { fromWinNumber?: unknown[] } | null)?.fromWinNumber
  const wipWupMinutes = themeConfig?.settings?.wipWupMinutes ?? 15
  const wipWupMinutesOther = themeConfig?.settings?.wipWupMinutesOther ?? 15
  const themeFolder = useMemo(
    () => getThemeById(themeConfig.themeId)?.folder ?? 'HuayToday-Blue',
    [themeConfig.themeId]
  )
  const [rounds, setRounds] = useState<LotteryRound[]>([])
  const [huayTypes, setHuayTypes] = useState<PublicHuayType[]>([])
  const [loading, setLoading] = useState(true)
  const isInitialLoadRef = useRef(true)
  const realtimeSyncTimeoutRef = useRef<number | null>(null)
  /** ประเภทหวยที่แอดมินปิด (จาก WebSocket huay_type_updated) — ซ่อน section ทั้งบล็อก */
  const [disabledHuayTypeNames, setDisabledHuayTypeNames] = useState<Set<string>>(() => new Set())
  /** Live YouTube popup state สำหรับการ์ดหวย */
  const huayYoutubeLive = themeConfig?.settings?.huayYoutubeLive
  const [livePopup, setLivePopup] = useState<{ title: string; youtubeUrl: string } | null>(null)
  const openLivePopup = useCallback((round: LotteryRound, entry: HuayYoutubeLiveConfig) => {
    setLivePopup({
      title: entry.label?.trim() || round.huayName || 'ถ่ายทอดสด',
      youtubeUrl: entry.youtubeUrl,
    })
  }, [])
  const closeLivePopup = useCallback(() => setLivePopup(null), [])

  /** เมื่อเข้าหน้านี้ (เช่น มาจากปุ่ม "แทงหวยเลย") ให้เลื่อนไปด้านบน ไม่ใช้ตำแหน่ง scroll เดิม */
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const getAssetUrl = useCallback(
    (path: string) => {
      const base = assetConfig.CDN_BASE_URL
        ? `${assetConfig.CDN_BASE_URL}/${themeFolder}`
        : `/${themeFolder}`
      return base + (path.startsWith('/') ? path : `/${path}`)
    },
    [assetConfig.CDN_BASE_URL, themeFolder]
  )

  /** ถ้าเป็น URL เต็ม (http/https) ใช้ตามนั้น ไม่ต้องใส่ theme path */
  const getRoundImageSrc = useCallback(
    (round: LotteryRound) => {
      const u = getRoundImage(round)
      if (/^https?:\/\//i.test(u)) return u
      return getAssetUrl(u)
    },
    [getAssetUrl]
  )

  const resolveImageSrc = useCallback(
    (value: string | null | undefined, fallbackAsset: string) => {
      const trimmed = String(value || '').trim()
      if (trimmed) {
        if (/^https?:\/\//i.test(trimmed)) return trimmed
        if (trimmed.startsWith('/')) return getAssetUrl(trimmed)
        return trimmed
      }
      return getAssetUrl(fallbackAsset)
    },
    [getAssetUrl]
  )

  const handleImgError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const el = e.currentTarget
    if (el.dataset.imgFallback === '1') return
    el.dataset.imgFallback = '1'
    el.src = IMG_PLACEHOLDER
  }, [])

  const loadRounds = useCallback(async (cacheBust = false) => {
    const showLoading = isInitialLoadRef.current
    if (showLoading) {
      isInitialLoadRef.current = false
      setLoading(true)
    }
    try {
      const data = await lotteryService.getTodayRounds(cacheBust)
      setRounds(data)
    } catch (err) {
      console.error('Error loading rounds:', err)
    } finally {
      if (showLoading) setLoading(false)
    }
  }, [])

  const loadHuayTypes = useCallback(async () => {
    try {
      const data = await lotteryService.getTenantHuayTypes({ limit: 200 })
      setHuayTypes(data)
    } catch (err) {
      console.error('Error loading huay types:', err)
    }
  }, [])

  const pollIntervalMs = typeof import.meta.env.DEV !== 'undefined' && import.meta.env.DEV ? 15_000 : 5_000
  useEffect(() => {
    loadRounds()
    loadHuayTypes()
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        loadRounds(true)
      }
    }, pollIntervalMs)
    return () => clearInterval(id)
  }, [loadRounds, loadHuayTypes, pollIntervalMs])

  const handleRoundRealtimeUpdate = useCallback((event?: Event) => {
    const detail = (event as CustomEvent<Record<string, unknown> | undefined> | undefined)?.detail
    const type =
      typeof detail?.type === 'string'
        ? detail.type
        : typeof detail?.sourceType === 'string'
          ? detail.sourceType
          : ''
    const roundDetail = (detail?.round ?? detail?.data) as LotteryRound | undefined

    if (roundDetail?._id) {
      setRounds((prev) => {
        const index = prev.findIndex((round) => round._id === roundDetail._id)
        if (index === -1) return prev

        const next = [...prev]
        next[index] = {
          ...next[index],
          ...roundDetail,
        }
        return next
      })
      if (realtimeSyncTimeoutRef.current) {
        window.clearTimeout(realtimeSyncTimeoutRef.current)
      }
      // Do not refetch immediately: some public round reads can lag behind the websocket payload
      // and would overwrite the fresh realtime state with stale data.
      realtimeSyncTimeoutRef.current = window.setTimeout(() => {
        loadRounds(true)
      }, 5000)
      return
    }

    if (!type || type === 'lottery_round_updated' || type === 'lottery_round_update') {
      loadRounds(true)
    }
  }, [loadRounds])

  useEffect(() => {
    const handleHuayTypeUpdated = (e: Event) => {
      const detail = (e as CustomEvent<{ name?: string; isActive?: boolean }>)?.detail
      const name = detail?.name
      if (name) {
        setDisabledHuayTypeNames((prev) => {
          const next = new Set(prev)
          if (detail.isActive === false) next.add(name)
          else if (detail.isActive === true) next.delete(name)
          return next
        })
      }
      loadHuayTypes()
      loadRounds(true)
    }
    window.addEventListener('lottery_round_updated', handleRoundRealtimeUpdate)
    window.addEventListener('lottery_round_update', handleRoundRealtimeUpdate)
    window.addEventListener('content_update', handleRoundRealtimeUpdate)
    window.addEventListener('huay_type_updated', handleHuayTypeUpdated)
    return () => {
      if (realtimeSyncTimeoutRef.current) {
        window.clearTimeout(realtimeSyncTimeoutRef.current)
      }
      window.removeEventListener('lottery_round_updated', handleRoundRealtimeUpdate)
      window.removeEventListener('lottery_round_update', handleRoundRealtimeUpdate)
      window.removeEventListener('content_update', handleRoundRealtimeUpdate)
      window.removeEventListener('huay_type_updated', handleHuayTypeUpdated)
    }
  }, [handleRoundRealtimeUpdate, loadHuayTypes, loadRounds])

  const huayTypeImageMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const huayType of huayTypes) {
      const image = String(huayType.imageUrl || huayType.imageURL || '').trim()
      if (!image) continue
      if (huayType.name) map.set(normalizeLabel(huayType.name), image)
      if (huayType.code) map.set(normalizeLabel(huayType.code), image)
    }
    return map
  }, [huayTypes])

  const getHuayTypeImageSrc = useCallback(
    (aliases: string[], fallbackAsset: string) => {
      for (const alias of aliases) {
        const matched = huayTypeImageMap.get(normalizeLabel(alias))
        if (matched) {
          return resolveImageSrc(matched, fallbackAsset)
        }
      }
      return getAssetUrl(fallbackAsset)
    },
    [getAssetUrl, huayTypeImageMap, resolveImageSrc]
  )

  // เรียงลำดับ: เปิดรับ (เรียงตามเวลาปิดรับ) → ยังไม่เปิด → ปิดรับแล้ว (ท้ายสุด)
  const roundsByType = useMemo(() => {
    const grouped = lotteryService.groupRoundsByType(rounds)
    for (const key of Object.keys(grouped)) {
      grouped[key] = lotteryService.sortRoundsForLottoDisplay(grouped[key])
    }
    return grouped
  }, [rounds])

  /** ประเภทที่มี section เฉพาะแล้ว — ประเภทที่เหลือจาก API จะไปแสดงในบล็อก dynamic ด้านล่าง */
  const KNOWN_SECTION_TYPE_NAMES = useMemo(
    () =>
      new Set([
        'หวยรัฐบาลไทย',
        'หวยยี่กี',
        'หวยยีกี่',
        'ยี่กี',
        'ยีกี่',
        'หวยต่างประเทศ',
        'หวยหุ้น VIP',
        'หวย VIP',
        'หวยหุ้น',
        'หวยชุด',
        'หวย VISA',
      ]),
    []
  )
  const otherTypeNames = useMemo(
    () => Object.keys(roundsByType).filter((k) => !KNOWN_SECTION_TYPE_NAMES.has(k)),
    [roundsByType, KNOWN_SECTION_TYPE_NAMES]
  )

  const govRounds = useMemo(
    () =>
      (roundsByType['หวยรัฐบาลไทย'] || []).filter(
        (r) =>
          r.huayName.includes('รัฐบาลไทย') &&
          !r.huayName.includes('ออมสิน') &&
          !r.huayName.includes('ธกส') &&
          !r.huayName.includes('ธ.ก.ส.')
      ),
    [roundsByType]
  )

  const yeekeeCount = useMemo(
    () =>
      (
        roundsByType['หวยยี่กี'] ||
        roundsByType['หวยยีกี่'] ||
        roundsByType['ยี่กี'] ||
        roundsByType['ยีกี่'] ||
        []
      ).length,
    [roundsByType]
  )

  const bankConfigs = useMemo(
    () => [
      { searchNames: ['ธ.ก.ส.', 'ธกส'], displayName: 'หวย ธ.ก.ส.', img: '/img/flag/egov.png' },
      { searchNames: ['ออมสิน'], displayName: 'หวยออมสิน', img: '/img/flag/gsb.png' },
    ],
    []
  )

  const governmentTypeImage = useMemo(
    () => getHuayTypeImageSrc(['หวยรัฐบาลไทย', 'หวยรัฐบาล'], '/img/flag/th.png'),
    [getHuayTypeImageSrc]
  )
  const yeekeeTypeImage = useMemo(
    () => getHuayTypeImageSrc(['หวยยี่กี', 'หวยยีกี่', 'ยี่กี', 'ยีกี่', 'yeekee'], '/img/yk.jpg'),
    [getHuayTypeImageSrc]
  )
  const bankSectionImage = governmentTypeImage
  const foreignTypeImage = useMemo(
    () => getHuayTypeImageSrc(['หวยต่างประเทศ'], '/img/logo-t.png'),
    [getHuayTypeImageSrc]
  )
  const vipTypeImage = useMemo(
    () => getHuayTypeImageSrc(['หวยหุ้น VIP', 'หวย VIP'], '/img/logo-t.png'),
    [getHuayTypeImageSrc]
  )
  const stockTypeImage = useMemo(
    () => getHuayTypeImageSrc(['หวยหุ้น', 'หวยหุ้นไทย', 'หวยหุ้นต่างประเทศ'], '/img/logo-t.png'),
    [getHuayTypeImageSrc]
  )
  const setTypeImage = useMemo(
    () => getHuayTypeImageSrc(['หวยชุด'], '/img/logo-t.png'),
    [getHuayTypeImageSrc]
  )
  const visaTypeImage = useMemo(
    () => getHuayTypeImageSrc(['หวย VISA'], '/img/logo-t.png'),
    [getHuayTypeImageSrc]
  )

  const handleRoundClick = useCallback(
    (round: LotteryRound, isYeekeeName: boolean) => {
      if (isRoundNotYetOpen(round)) {
        void Swal.fire({
          icon: 'info',
          title: 'ยังไม่เปิดรับแทง รอเวลาเปิด',
          toast: true,
          position: 'top',
          timer: 2500,
          showConfirmButton: false,
        })
        return
      }
      const active = canRoundAcceptBet(round)
      if (active) {
        navigate('/lobby-huay/lottery-terms', { state: { round, ...(fromWinNumber && { fromWinNumber }) } })
      } else if (isYeekeeName) {
        navigate(`/lobby-huay/yeekee/result?roundId=${round._id}`, { state: { round } })
      } else {
        navigate(`/lobby-huay/result?roundId=${round._id}`, { state: { round, isYeekeeName } })
      }
    },
    [navigate, fromWinNumber]
  )

  /** การ์ดรอบหวย สำหรับ หวยธนาคาร (col-6 col-md-6, gap-2, ไม่ใช้ template แบบหวยต่างประเทศ) */
  const renderRoundCard = (
    round: LotteryRound,
    key: string,
    opts: { isYeekeeName?: boolean; bgClass?: string; timeClass?: string } = {}
  ) => {
    const notYetOpen = isRoundNotYetOpen(round)
    const active = canRoundAcceptBet(round) && !notYetOpen
    const isYeekeeName = opts.isYeekeeName ?? /ยี่กี|ยี/.test((round.huayTypeName || '') + (round.huayName || ''))
    const bgClass = opts.bgClass ?? (active ? 'bg_gd_quinary' : 'bg_gd_secondary')
    const timeClass = isRoundCancelled(round)
      ? 'c_warning'
      : (opts.timeClass ?? getLottoRoundStatusColorClass(round, active, notYetOpen))
    const closedCardClass = getLottoRoundClosedCardClass(round, active)
    const liveEntry = lookupHuayYoutubeLiveForRound(round, huayYoutubeLive)

    return (
      <div key={key} className={CARD_COL}>
        <div className={CARD_STACK}>
          <div
            role="button"
            tabIndex={0}
            className={`${CARD_BASE} ${closedCardClass} ${bgClass} border_5 p-1 h-100`}
            style={{ cursor: 'pointer', position: 'relative' }}
            onClick={() => handleRoundClick(round, isYeekeeName)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleRoundClick(round, isYeekeeName)
              }
            }}
          >
            {liveEntry && (
              <HuayLiveBadge
                round={round}
                onOpen={(r) => openLivePopup(r, liveEntry)}
              />
            )}
            <div className="bg_op_tertiary border_5 p-1 lobby-huay-round-card__body">
              <div className={`s_huay box_row lobby-huay-round-card__head ${bgClass} border_5 px-1 gap-2`}>
                <div className="img_sh">
                  <img
                    src={getRoundImageSrc(round)}
                    alt=""
                    onError={handleImgError}
                  />
                </div>
                <h6 className="txt_sh c_tertiary">{round.huayName}</h6>
              </div>
              {notYetOpen && <h6 className="text-center font_sm mb-0 c_tertiary lobby-huay-round-card__status">ยังไม่เปิดรับแทง</h6>}
              <h5 className={`text-center font_md mb-0 lobby-huay-round-card__countdown ${timeClass}`}>
                <CountdownDisplay round={round} />
              </h5>
            </div>
            <h5 className="text-center txt_mb font_lg c_tertiary mb-0 lobby-huay-round-card__footer">
              {notYetOpen ? `เปิดรับ ${formatCloseTime(round.betStartTime)}` : `ปิดรับ ${formatCloseTime(round.betEndTime)}`}
            </h5>
          </div>
        </div>
      </div>
    )
  }

  /** การ์ดรอบหวย — wip_wup แสดงเมื่อเหลือเวลาน้อยกว่าที่กำหนด (ยี่กีใช้ wipWupMinutes, อื่นๆ ใช้ wipWupMinutesOther) */
  const renderRoundCardGrid = (
    round: LotteryRound,
    key: string,
    opts: { bgClass?: string; timeClass?: string; titleClass?: string } = {}
  ) => {
    const notYetOpen = isRoundNotYetOpen(round)
    const active = canRoundAcceptBet(round) && !notYetOpen
    const minutesRemaining = (new Date(round.betEndTime).getTime() - Date.now()) / (1000 * 60)
    const isYeekeeName = /ยี่กี|ยี/.test((round.huayTypeName || '') + (round.huayName || ''))
    const threshold = isYeekeeName ? wipWupMinutes : wipWupMinutesOther
    const isLastMinutes = active && minutesRemaining <= threshold && minutesRemaining > 0
    const bgClass = opts.bgClass ?? 'bg_gd_warning'
    const timeClass = isRoundCancelled(round)
      ? 'c_warning'
      : (opts.timeClass ?? (active ? 'c_primary' : notYetOpen ? 'c_tertiary' : 'c_danger'))
    const titleClass = opts.titleClass ?? 'c_secondary'
    const closedCardClass = getLottoRoundClosedCardClass(round, active)
    const liveEntry = lookupHuayYoutubeLiveForRound(round, huayYoutubeLive)

    return (
      <div key={key} className={CARD_COL_GRID}>
        <div
          role="button"
          tabIndex={0}
          className={`${CARD_BASE} ${closedCardClass} ${bgClass} border_5 p-1 h-100`}
          style={{ cursor: 'pointer', position: 'relative' }}
          onClick={() => handleRoundClick(round, isYeekeeName)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleRoundClick(round, isYeekeeName)
            }
          }}
        >
          {liveEntry && (
            <HuayLiveBadge
              round={round}
              onOpen={(r) => openLivePopup(r, liveEntry)}
            />
          )}
          <div className="bg_op_tertiary border_5 p-1 lobby-huay-round-card__body">
            <div className={`s_huay box_row lobby-huay-round-card__head ${bgClass} border_5 px-1 gap-1`}>
              <div className={`img_sh ${isLastMinutes ? 'wip_wup' : ''}`}>
                <img
                  src={getRoundImageSrc(round)}
                  alt=""
                  onError={handleImgError}
                />
              </div>
              <h6 className={`txt_sh ${titleClass} ${isLastMinutes ? 'wip_wup' : ''}`}>
                {round.huayName}
              </h6>
            </div>
            {notYetOpen && <h6 className="text-center font_sm mb-0 c_tertiary lobby-huay-round-card__status">ยังไม่เปิดรับแทง</h6>}
            <h5 className={`text-center font_md mb-0 lobby-huay-round-card__countdown ${timeClass} ${isLastMinutes ? 'wip_wup' : ''}`}>
              <CountdownDisplay round={round} />
            </h5>
          </div>
          <h5 className="text-center txt_mb font_lg c_tertiary mb-0 lobby-huay-round-card__footer">
            {notYetOpen ? `เปิดรับ ${formatCloseTime(round.betStartTime)}` : `ปิดรับ ${formatCloseTime(round.betEndTime)}`}
          </h5>
        </div>
      </div>
    )
  }

  const sectionTitle = (imgSrc: string, label: string) => (
    <div className="col-12 px-1">
      <div className="h_huay box_row gap-2">
        <div className="img_hh">
          <img src={imgSrc} alt="" onError={handleImgError} />
        </div>
        <h5 className="txt_hh c_tertiary">{label}</h5>
      </div>
    </div>
  )

  const loadingSpinner = (
    <div className="col-12 text-center py-4">
      <i className="fas fa-spinner fa-spin fs-24 c_tertiary" />
    </div>
  )

  const emptyMessage = (msg: string) => (
    <div className="col-12 text-center py-4">
      <p className="c_tertiary">{msg}</p>
    </div>
  )

  return (
    <>
      <div className="lobby-huay-listall">
      <div className="c_quinary text-center">
        <h3 className="c_tertiary font_md">รายการหวยวันนี้</h3>
      </div>
      {fromWinNumber && fromWinNumber.length > 0 && (
        <div className="bg_gd_quaternary border_5 p-2 mt-2 text-center">
          <small className="c_tertiary">
            <i className="fas fa-info-circle me-1" />
            คุณมีเลขจับวินรออยู่ ({fromWinNumber.length} รายการ) — กรุณาเลือกหวยที่ต้องการแทงด้านล่าง แล้วเลือกแพ็คเกจ ระบบจะนำเลขไปใส่ตารางแทงให้
          </small>
        </div>
      )}
      <div className="bg_op_secondary border_5 p-2 d-grid mt-3" style={{ rowGap: '0.5rem' }}>
        {/* หวยรัฐบาล + จับยี่กี (2 คอลัมน์) */}
        <div className="row mx__1" style={{ rowGap: '0.5rem' }}>
          {/* หวยรัฐบาลไทย — จาก API */}
          <div className="col-6 col-md-6 px-1 lobby-huay-col">
            <div className={CARD_STACK}>
              <div className="h_huay box_row gap-2">
                <div className="img_hh">
                  <img src={governmentTypeImage} alt="" onError={handleImgError} />
                </div>
                <h5 className="txt_hh c_tertiary">หวยรัฐบาล</h5>
              </div>
              {loading ? (
                loadingSpinner
              ) : govRounds.length > 0 ? (
                (() => {
                  const round = govRounds[0]
                  const notYetOpen = isRoundNotYetOpen(round)
                  const active = canRoundAcceptBet(round) && !notYetOpen
                  const minutesRemaining = (new Date(round.betEndTime).getTime() - Date.now()) / (1000 * 60)
                  const isLastMinutes = active && minutesRemaining <= wipWupMinutesOther && minutesRemaining > 0
                  const liveEntry = lookupHuayYoutubeLiveForRound(round, huayYoutubeLive)
                  const closedCardClass = getLottoRoundClosedCardClass(round, active)
                  const countdownClass = getLottoRoundStatusColorClass(round, active, notYetOpen, {
                    active: 'c_quaternary',
                  })
                  return (
                    <div
                      role="button"
                      tabIndex={0}
                      className={`${CARD_BASE} ${closedCardClass} ${active ? 'bg_gd_quinary' : 'bg_gd_secondary'} border_5 p-1 h-100`}
                      style={{ cursor: 'pointer', position: 'relative' }}
                      onClick={() => handleRoundClick(round, false)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          handleRoundClick(round, false)
                        }
                      }}
                    >
                      {liveEntry && (
                        <HuayLiveBadge
                          round={round}
                          onOpen={(r) => openLivePopup(r, liveEntry)}
                        />
                      )}
                      <div className="bg_op_tertiary border_5 p-1 lobby-huay-round-card__body">
                        <div className="s_huay box_row lobby-huay-round-card__head bg_gd_quinary border_5 px-1 gap-2">
                          <div className={`img_sh ${isLastMinutes ? 'wip_wup' : ''}`}>
                            <img src={governmentTypeImage} alt="" onError={handleImgError} />
                          </div>
                          <h6 className={`txt_sh c_tertiary ${isLastMinutes ? 'wip_wup' : ''}`}>{round.huayName}</h6>
                        </div>
                        {notYetOpen && <h6 className="text-center font_sm mb-0 c_tertiary lobby-huay-round-card__status">ยังไม่เปิดรับแทง</h6>}
                        <h5 className={`text-center font_md mb-0 lobby-huay-round-card__countdown ${countdownClass} ${isLastMinutes ? 'wip_wup' : ''}`}>
                          <CountdownDisplay round={round} />
                        </h5>
                      </div>
                      <h5 className="text-center txt_mb font_lg c_tertiary mb-0 lobby-huay-round-card__footer">
                        {notYetOpen ? `เปิดรับ ${formatCloseTime(round.betStartTime)}` : `ปิดรับ ${formatCloseTime(round.betEndTime)}`}
                      </h5>
                    </div>
                  )
                })()
              ) : (
                <div className={`${CARD_BASE} bg_gd_secondary border_5 p-1 h-100`}>
                  <div className="bg_op_tertiary border_5 p-1 lobby-huay-round-card__body">
                    <div className="s_huay box_row lobby-huay-round-card__head bg_gd_secondary border_5 px-1 gap-2">
                      <div className="img_sh">
                        <img src={governmentTypeImage} alt="" onError={handleImgError} />
                      </div>
                      <h6 className="txt_sh c_tertiary">หวยรัฐบาลไทย</h6>
                    </div>
                    <h5 className="text-center font_md mb-0 c_danger lobby-huay-round-card__countdown">ปิดรับแทง</h5>
                  </div>
                  <h5 className="text-center txt_mb font_lg c_tertiary mb-0 lobby-huay-round-card__footer">ไม่มีรอบหวย</h5>
                </div>
              )}
            </div>
          </div>

          {/* จับยี่กี — ลิงก์ไป yeekee */}
          <div className="col-6 col-md-6 px-1 lobby-huay-col">
            <div className={CARD_STACK}>
              <div className="h_huay box_row gap-2">
                <div className="img_hh">
                  <img src={yeekeeTypeImage} alt="" onError={handleImgError} />
                </div>
                <h5 className="txt_hh c_tertiary">หวยจับยี่กี</h5>
              </div>
              <Link to="/lobby-huay/yeekee" className="text-decoration-none flex-grow-1 d-flex">
                <div className={`${CARD_BASE} bg_gd_danger border_5 p-1 h-100 w-100`}>
                  <div className="bg_op_tertiary border_5 p-1 lobby-huay-round-card__body">
                    <div className="s_huay box_row lobby-huay-round-card__head bg_gd_danger border_5 px-1 gap-2">
                      <h6 className="txt_sh c_tertiary">หวยยี่กี VIP</h6>
                    </div>
                    <h5 className="text-center font_md mb-0 c_danger lobby-huay-round-card__countdown">24 ชั่วโมง</h5>
                  </div>
                  <h5 className="text-center txt_mb font_lg c_tertiary mb-0 lobby-huay-round-card__footer">
                    {loading ? (
                      <i className="fas fa-spinner fa-spin" />
                    ) : (
                      `เปิดแทง ${yeekeeCount} รอบ`
                    )}
                  </h5>
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* หวยธนาคาร */}
        <div className="row mx__1" style={{ rowGap: '0.5rem' }}>
          {sectionTitle(bankSectionImage, 'หวยธนาคาร')}
          {loading ? (
            loadingSpinner
          ) : (
            bankConfigs.map((config, idx) => {
              const round = rounds.find((r) =>
                config.searchNames.some((name) => r.huayName.includes(name))
              )
              if (!round) {
                return (
                  <div key={`bank-${idx}`} className={CARD_COL}>
                    <div className={`${CARD_BASE} bg_gd_secondary border_5 p-1 h-100`}>
                      <div className="bg_op_tertiary border_5 p-1 lobby-huay-round-card__body">
                        <div className="s_huay box_row lobby-huay-round-card__head bg_gd_success border_5 px-1 gap-2">
                          <div className="img_sh">
                            <img src={getAssetUrl(config.img)} alt="" onError={handleImgError} />
                          </div>
                          <h6 className="txt_sh c_tertiary">{config.displayName}</h6>
                        </div>
                        <h5 className="text-center font_md mb-0 c_danger lobby-huay-round-card__countdown">ปิดรับแทง</h5>
                      </div>
                      <h5 className="text-center txt_mb font_lg c_tertiary mb-0 lobby-huay-round-card__footer">ไม่มีรอบหวย</h5>
                    </div>
                  </div>
                )
              }
              const bankActive = canRoundAcceptBet(round) && !isRoundNotYetOpen(round)
              return renderRoundCard(round, round._id, {
                bgClass: bankActive ? 'bg_gd_success' : 'bg_gd_secondary',
                timeClass: bankActive ? 'c_success' : isRoundNotYetOpen(round) ? 'c_tertiary' : 'c_danger',
              })
            })
          )}
        </div>

        {/* หวยต่างประเทศ — ซ่อนทั้ง section เมื่อแอดมินปิดประเภทนี้ */}
        {!disabledHuayTypeNames.has('หวยต่างประเทศ') && (
          <div className="row mx__1" style={{ rowGap: '0.5rem' }}>
            {sectionTitle(foreignTypeImage, 'หวยต่างประเทศ')}
            {loading ? (
              loadingSpinner
            ) : (roundsByType['หวยต่างประเทศ'] || []).length > 0 ? (
              (roundsByType['หวยต่างประเทศ'] || []).map((round) =>
                renderRoundCardGrid(round, round._id, { bgClass: 'bg_gd_warning', timeClass: 'c_primary' })
              )
            ) : (
              emptyMessage('ไม่มีรอบหวยในวันนี้')
            )}
          </div>
        )}

        {/* หวยหุ้น VIP — ซ่อนเมื่อปิดประเภท หวยหุ้น VIP หรือ หวย VIP */}
        {!disabledHuayTypeNames.has('หวยหุ้น VIP') && !disabledHuayTypeNames.has('หวย VIP') && (
          <div className="row mx__1" style={{ rowGap: '0.5rem' }}>
            {sectionTitle(vipTypeImage, 'หวยหุ้น VIP')}
            {loading ? (
              loadingSpinner
            ) : (roundsByType['หวยหุ้น VIP'] || roundsByType['หวย VIP'] || []).length > 0 ? (
              (roundsByType['หวยหุ้น VIP'] || roundsByType['หวย VIP'] || []).map((round) =>
                renderRoundCardGrid(round, round._id)
              )
            ) : (
              emptyMessage('ไม่มีรอบหวยในวันนี้')
            )}
          </div>
        )}

        {/* หวยหุ้นต่างประเทศ — ซ่อนเมื่อปิดประเภท หวยหุ้น */}
        {!disabledHuayTypeNames.has('หวยหุ้น') && (
          <div className="row mx__1" style={{ rowGap: '0.5rem' }}>
            {sectionTitle(stockTypeImage, 'หวยหุ้นต่างประเทศ')}
            {loading ? (
              loadingSpinner
            ) : (roundsByType['หวยหุ้น'] || []).filter((r) => !r.huayName.includes('ไทย')).length > 0 ? (
              (roundsByType['หวยหุ้น'] || [])
                .filter((r) => !r.huayName.includes('ไทย'))
                .map((round) => renderRoundCardGrid(round, round._id))
            ) : (
              emptyMessage('ไม่มีรอบหวยในวันนี้')
            )}
          </div>
        )}

        {/* หวยชุด — ซ่อนเมื่อปิดประเภท */}
        {!disabledHuayTypeNames.has('หวยชุด') && (
          <div className="row mx__1" style={{ rowGap: '0.5rem' }}>
            {sectionTitle(setTypeImage, 'หวยชุด')}
            {loading ? (
              loadingSpinner
            ) : (roundsByType['หวยชุด'] || []).length > 0 ? (
              (roundsByType['หวยชุด'] || []).map((round) =>
                renderRoundCardGrid(round, round._id, { bgClass: 'bg_gd_info', timeClass: 'c_quaternary' })
              )
            ) : (
              emptyMessage('ไม่มีรอบหวยในวันนี้')
            )}
          </div>
        )}

        {/* หวยหุ้นไทย — ซ่อนเมื่อปิดประเภท หวยหุ้น */}
        {!disabledHuayTypeNames.has('หวยหุ้น') && (
          <div className="row mx__1" style={{ rowGap: '0.5rem' }}>
            {sectionTitle(stockTypeImage, 'หวยหุ้นไทย')}
            {loading ? (
              loadingSpinner
            ) : (roundsByType['หวยหุ้น'] || []).filter((r) => r.huayName.includes('ไทย')).length > 0 ? (
              (roundsByType['หวยหุ้น'] || [])
                .filter((r) => r.huayName.includes('ไทย'))
                .map((round) => renderRoundCardGrid(round, round._id))
            ) : (
              emptyMessage('ไม่มีรอบหวยในวันนี้')
            )}
          </div>
        )}

        {/* หวย VISA — ซ่อนเมื่อปิดประเภท */}
        {!disabledHuayTypeNames.has('หวย VISA') && (
          <div className="row mx__1" style={{ rowGap: '0.5rem' }}>
            {sectionTitle(visaTypeImage, 'หวย VISA')}
            {loading ? (
              loadingSpinner
            ) : (roundsByType['หวย VISA'] || []).length > 0 ? (
              (roundsByType['หวย VISA'] || []).map((round) =>
                renderRoundCardGrid(round, round._id, { bgClass: 'bg_gd_quinary', timeClass: 'c_quaternary', titleClass: 'c_tertiary' })
              )
            ) : (
              emptyMessage('ไม่มีรอบหวยในวันนี้')
            )}
          </div>
        )}

        {/* ประเภทหวยอื่นๆ จาก API — เพิ่มประเภทใหม่ใน admin จะแสดงที่นี่โดยไม่ต้องแก้ frontend */}
        {otherTypeNames
          .filter((typeName) => !disabledHuayTypeNames.has(typeName))
          .map((typeName) => (
            <div key={typeName} className="row mx__1" style={{ rowGap: '0.5rem' }}>
              {sectionTitle(getHuayTypeImageSrc([typeName], '/img/logo-t.png'), typeName)}
              {loading ? (
                loadingSpinner
              ) : (roundsByType[typeName] || []).length > 0 ? (
                (roundsByType[typeName] || []).map((round) =>
                  renderRoundCardGrid(round, round._id, { bgClass: 'bg_gd_secondary', timeClass: 'c_tertiary' })
                )
              ) : (
                emptyMessage('ไม่มีรอบหวยในวันนี้')
              )}
            </div>
          ))}
      </div>
      </div>
      <HuayLiveModal
        isOpen={livePopup !== null}
        onClose={closeLivePopup}
        title={livePopup?.title || ''}
        youtubeUrl={livePopup?.youtubeUrl || ''}
        playerStartMuted={themeConfig?.settings?.liveTvPlayerStartMuted !== false}
      />
    </>
  )
}

export default ModernLobbyHuayListAllPage
